import type { AssetMap } from "../assets/loader";
import {
  fetchLeaderboard,
  getPlayerName,
  setPlayerName,
  submitLeaderboardScore,
  startSession,
  stopHeartbeat,
} from "../ui/leaderboard";
import { Screens } from "../ui/screens";
import { AudioBus } from "./Audio";
import { Input } from "./Input";
import { World } from "./World";
import {
  isTelegram,
  getTelegramUser,
  tgHaptic,
  tgShowBackButton,
  tgOnBackButton,
  tgOffBackButton,
} from "../telegram";

export type GameState = "menu" | "playing" | "paused" | "gameover";

const FIXED_DT = 1 / 60;
const MAX_FRAME = 0.05;

export class Game {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly world: World;
  private readonly input: Input;
  private readonly audio = new AudioBus();
  private readonly screens = new Screens();
  private state: GameState = "menu";
  private raf = 0;
  private acc = 0;
  private last = 0;
  private running = false;
  private lastScore = 0;
  private boardReturn: "menu" | "gameover" = "menu";
  private nameResolver: (() => void) | null = null;
  private backButtonCb: (() => void) | null = null;

  constructor(canvas: HTMLCanvasElement, assets: AssetMap) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D unavailable");
    this.ctx = ctx;
    this.world = new World(assets);
    this.input = new Input(canvas);
    this.bindUi();
    this.syncMuteButton();

    const tgUser = getTelegramUser();
    if (tgUser) {
      setPlayerName(tgUser.firstName);
    }
    this.screens.setMenuPlayer(getPlayerName());
    this.fitCanvas();
    window.addEventListener("resize", this.fitCanvas);
  }

  start(): void {
    this.screens.show("menu");
    this.state = "menu";
    this.running = true;
    this.last = performance.now();
    this.loop(this.last);
  }

  private fitCanvas = (): void => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width * dpr));
    const h = Math.max(1, Math.floor(rect.height * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.ctx.setTransform(w / 1280, 0, 0, h / 720, 0, 0);
    // Smooth scaling keeps cel-shaded sprites looking clean (not chunky rectangles)
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = "high";
  };

  private bindUi(): void {
    const on = (id: string, fn: () => void) => {
      document.getElementById(id)?.addEventListener("click", () => {
        this.audio.ui();
        fn();
      });
    };

    on("btn-play", () => { this.audio.start(); this.play(); });
    on("btn-retry", () => { this.audio.start(); this.play(); });
    on("btn-resume", () => this.resume());
    on("btn-quit", () => this.toMenu());
    on("btn-go-menu", () => this.toMenu());
    on("btn-leaderboard", () => void this.openBoard("menu"));
    on("btn-go-board", () => void this.openBoard("gameover"));

    // Space/Enter starts/retries from menu or gameover
    window.addEventListener("keydown", (e) => {
      if (e.repeat) return;
      if (e.code === "Space" || e.code === "Enter") {
        if (this.state === "menu" || this.state === "gameover") {
          e.preventDefault();
          this.audio.start();
          this.play();
        }
      }
    });
    on("btn-board-close", () => {
      if (this.boardReturn === "gameover") {
        this.screens.show("gameover");
        this.state = "gameover";
      } else {
        this.screens.show("menu");
        this.state = "menu";
      }
    });
    on("btn-name", () => {
      const input = document.getElementById("name-input") as HTMLInputElement | null;
      if (input) input.value = getPlayerName();
      this.screens.openOverlay("name", "menu");
    });
    on("btn-how", () => {
      if (this.state === "playing") this.pause();
      const from =
        this.state === "paused"
          ? "pause"
          : this.state === "gameover"
            ? "gameover"
            : "menu";
      this.screens.openOverlay("how", from);
    });
    on("btn-how-close", () => this.screens.closeOverlay());
    on("btn-mute", () => {
      this.audio.setMuted(!this.audio.muted);
      this.syncMuteButton();
    });

    document.getElementById("name-form")?.addEventListener("submit", (e) => {
      e.preventDefault();
      const input = document.getElementById("name-input") as HTMLInputElement;
      const name = input.value.trim();
      if (!/^[A-Za-z0-9 ]{1,16}$/.test(name)) {
        input.reportValidity();
        return;
      }
      setPlayerName(name);
      this.screens.setMenuPlayer(name);
      this.audio.ui();
      const resolver = this.nameResolver;
      this.nameResolver = null;
      if (resolver) {
        resolver();
        return;
      }
      this.screens.closeOverlay();
    });

    document.getElementById("btn-name-cancel")?.addEventListener("click", () => {
      const resolver = this.nameResolver;
      this.nameResolver = null;
      if (resolver) {
        resolver();
        return;
      }
      this.screens.closeOverlay();
    });
  }

  private syncMuteButton(): void {
    const btn = document.getElementById("btn-mute");
    if (!btn) return;
    btn.textContent = this.audio.muted ? "Muted" : "Sound";
    btn.setAttribute("aria-pressed", this.audio.muted ? "true" : "false");
  }

  private play(): void {
    this.world.reset();
    this.state = "playing";
    this.acc = 0;
    this.last = performance.now();
    this.input.clearBuffer();
    startSession(() => this.world.score.total);
    this.screens.show("none");
    this.screens.setHudVisible(true);
    this.screens.setHud(0, this.world.score.best);
    this.setBackButton(true, () => this.pause());
  }

  private resume(): void {
    this.state = "playing";
    this.screens.show("none");
    this.screens.setHudVisible(true);
    this.setBackButton(true, () => this.pause());
  }

  private toMenu(): void {
    this.state = "menu";
    this.screens.setHudVisible(false);
    this.screens.setMenuPlayer(getPlayerName());
    this.screens.show("menu");
    this.setBackButton(false, null);
  }

  private pause(): void {
    if (this.state !== "playing") return;
    this.state = "paused";
    this.screens.show("pause");
    this.setBackButton(true, () => this.toMenu());
  }

  private setBackButton(show: boolean, cb: (() => void) | null): void {
    if (this.backButtonCb) {
      tgOffBackButton(this.backButtonCb);
      this.backButtonCb = null;
    }
    tgShowBackButton(show);
    if (show && cb) {
      this.backButtonCb = cb;
      tgOnBackButton(cb);
    }
  }

  private async openBoard(from: "menu" | "gameover"): Promise<void> {
    this.boardReturn = from;
    this.screens.show("leaderboard");
    const list = document.getElementById("board-list");
    const status = document.getElementById("board-status");
    if (list) list.innerHTML = "";
    if (status) status.textContent = "Loading…";
    try {
      const scores = await fetchLeaderboard(20);
      if (status) status.textContent = scores.length ? "" : "No scores yet — be the first.";
      if (list) {
        const me = getPlayerName();
        list.innerHTML = scores
          .map(
            (s, i) =>
              `<li class="${s.name === me && s.score === this.lastScore ? "highlight" : ""}"><span>${i + 1}</span><span>${escapeHtml(s.name)}</span><span>${s.score}</span></li>`
          )
          .join("");
      }
    } catch {
      if (status) status.textContent = "Leaderboard offline. Is the server running?";
    }
  }

  private async onGameOver(): Promise<void> {
    this.state = "gameover";
    stopHeartbeat();
    this.lastScore = this.world.score.total;
    const deathMsg = this.world.score.getDeathMessage();
    this.screens.setHudVisible(false);
    this.screens.setGameOver(this.lastScore, "Submitting…", deathMsg);
    this.screens.show("gameover");
    this.setBackButton(true, () => this.toMenu());

    let name = getPlayerName();
    if (!name && !isTelegram) {
      const input = document.getElementById("name-input") as HTMLInputElement | null;
      if (input) input.value = "";
      await new Promise<void>((resolve) => {
        this.nameResolver = resolve;
        this.screens.openOverlay("name", "gameover");
      });
      name = getPlayerName();
      this.screens.show("gameover");
    }

    if (!name) {
      this.screens.setGameOver(
        this.lastScore,
        "Score saved locally only (no name)."
      );
      return;
    }

    try {
      const { rank } = await submitLeaderboardScore(name, this.lastScore);
      this.screens.setGameOver(this.lastScore, `Global rank #${rank}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Submit failed";
      this.screens.setGameOver(this.lastScore, msg);
    }
  }

  private loop = (now: number): void => {
    if (!this.running) return;
    const frame = Math.min(MAX_FRAME, (now - this.last) / 1000);
    this.last = now;
    this.acc += frame;

    this.input.beginFrame();

    if (this.input.pausePressed) {
      if (this.state === "playing") this.pause();
      else if (this.state === "paused") this.resume();
    }

    while (this.acc >= FIXED_DT) {
      if (this.state === "playing") {
        this.world.update(
          FIXED_DT,
          this.input.jumpPressed,
          this.input.jumpHeld,
          this.input.duckHeld,
          () => { this.audio.jump(); tgHaptic("jump"); },
          () => {
            if (this.world.lastPickupType === "powerup") {
              this.audio.powerUp();
            } else if (this.world.lastPickupType === "powerdown") {
              this.audio.powerDown();
            } else {
              this.audio.collect();
            }
            tgHaptic("collect");
          },
          () => {
            this.audio.hurt();
            tgHaptic("death");
            void this.onGameOver();
          },
          () => {
            this.audio.milestone();
            const msg = this.world.score.getMilestoneMessage();
            if (msg) this.screens.showToast(msg);
          }
        );
        // consume jump press after first physics step
        this.input.jumpPressed = false;
      } else if (this.state === "gameover") {
        this.world.decayShake(FIXED_DT);
      }
      this.acc -= FIXED_DT;
    }

    this.fitCanvas();
    this.world.draw(this.ctx);
    if (this.state === "playing" || this.state === "paused") {
      this.screens.setHud(this.world.score.total, this.world.score.best);
    }

    this.raf = requestAnimationFrame(this.loop);
  };
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
